import { useEffect, useState } from "react";
import { CrudPanel } from "../components/CrudPanel";
import { PhotoUploader, usePhotoUploader } from "../components/PhotoUploader";
import { roomService } from "../services/roomService";
import { propertyService } from "../services/propertyService";
import { seasonService } from "../services/seasonService";
import { pricingRuleService } from "../services/pricingRuleService";
import { BedDouble } from "lucide-react";
import { money } from "../utils/format";
import { photoSlots, thumbnailForRow, withPhotos } from "../utils/photos";
import {
  TIER_FIELDS,
  emptyTier,
  normalizeTier,
  regularRatesFor,
  roomKey,
} from "../utils/rateTiers";

function photoStateForRow(row) {
  const photos = photoSlots(row);
  const requested = Number(row?.thumbnailPhoto || 0) - 1;
  return {
    photos,
    thumbnailIndex: photos.length ? Math.min(Math.max(requested, 0), photos.length - 1) : 0,
  };
}

const emptyRoom = {
  propertyName: "",
  propertyUuid: "",
  roomName: "",
  roomNumber: "",
  roomType: "",
  maxOccupancy: 2,
  bedType: "",
  basePrice: 0,
  b2bPrice: 0,
  sellingPrice: 0,
  enableForAgents: false,
};

const TIER_LABELS = {
  weekend: "Weekend",
  holiday: "Holiday",
  peak: "Peak Season",
};

const PRICE_FIELDS = [
  { key: "b2b", label: "B2B Price", formKey: "b2bPrice" },
  { key: "selling", label: "Selling Price", formKey: "sellingPrice" },
];
const PRICE_COLORS = {
  b2b: "rgb(37 99 235 / var(--tw-text-opacity, 1))",
  selling: "rgb(5 150 105 / var(--tw-text-opacity, 1))",
};

const RULE_CONFIG = {
  weekend: { ruleType: "WEEKEND", ruleName: "Weekend", priority: 5 },
  holiday: { ruleType: "HOLIDAY", ruleName: "Holiday", priority: 8 },
  peak: { ruleType: "PEAK", ruleName: "Peak Season", priority: 10 },
};

const defaultRoomTypes = ["Standard", "Deluxe", "Suite", "Premium", "Economy"];
const defaultBedTypes = ["Single", "Double", "Queen", "King", "Twin"];

export function Rooms({ notify, setHeaderAction, tenantId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyRoom);
  const [showForm, setShowForm] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tier, setTier] = useState(emptyTier());
  const [tierKey, setTierKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const photos = usePhotoUploader({ notify });
  const formFields = ["propertyName", "propertyUuid", "roomName", "roomNumber", "roomType", "maxOccupancy", "bedType", "basePrice", "b2bPrice", "sellingPrice", "enableForAgents", "isDeleted"];

  useEffect(() => {
    propertyService.listForTenant(tenantId).then((list) => {
    setProperties(list);
    setPropertyOptions(list.map((p) => ({ value: p.uuid, label: p.name })));
    }).catch(() => setPropertyOptions([]));
  }, [tenantId]);

  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction(
        <button
          className={showForm ? "secondary" : "primary"}
          onClick={() => setShowForm(!showForm)}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {showForm ? "Hide" : "Add"} Room
        </button>
      );
    }
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [showForm, setHeaderAction]);

  const load = async () => {
    setLoading(true);
    try {
      const result = await roomService.list(tenantId);
      setRows(result);
    } catch (err) {
      notify(err?.message || "Failed to load rooms.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  async function save(event) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    const room = { ...form };
    try {
      const savedRoom = await roomService.save(withPhotos(room, photos.photos, photos.thumbnailIndex));

      if (tierKey) {
        await seasonService.saveRoomTier(tierKey, normalizeTier(tier, regularRatesFor(room)));
      }

      await saveSeasonalPricingRules(room, savedRoom);

      setForm(emptyRoom);
      setShowForm(false);
      setTier(emptyTier());
      setTierKey("");
      photos.clearPhotos();
      notify("Room and seasonal pricing saved.");
    } catch (err) {
      console.error("Room save error:", err);
      notify(err?.message || "Could not save room.");
    } finally {
      setSaving(false);
      load();
    }
  }

  async function saveSeasonalPricingRules(room, savedRoom) {
    let property = properties.find(
      (item) => item.uuid === room.propertyUuid || item.name === room.propertyName
    );
    if (!property && room.propertyUuid) {
      try {
        property = await propertyService.get(room.propertyUuid);
      } catch { property = null; }
    }
    const roomId = savedRoom?.id || savedRoom?.data?.id || room.id;

    if (!property?.id || !roomId) {
      throw new Error("Could not identify the property or room for seasonal pricing.");
    }

    const rulesResponse = await pricingRuleService.list(property.id);
    const rules = [...(rulesResponse?.content || [])];
    const regularPrices = regularRatesFor(room);

    for (const season of TIER_FIELDS) {
      const config = RULE_CONFIG[season];
      let rule = rules.find((item) => item.ruleType === config.ruleType);
      if (!rule) {
        rule = await pricingRuleService.create({
          propertyId: property.id,
          ruleName: config.ruleName,
          ruleType: config.ruleType,
          priority: config.priority,
          isEnabled: true,
        });
        rules.push(rule);
      }

      const b2b = Number(tier[season]?.b2b || 0) || Number(regularPrices.b2b || 0);
      const selling = Number(tier[season]?.selling || 0) || Number(regularPrices.selling || 0);
      const pricingValue = Number((selling - b2b).toFixed(2));
      if (pricingValue < 0) {
        throw new Error(`${TIER_LABELS[season]} selling price cannot be less than the B2B price.`);
      }

      const roomRulesResponse = await pricingRuleService.listRooms(rule.id);
      const alreadySaved = (roomRulesResponse?.content || []).some(
        (item) => String(item.roomId) === String(roomId)
      );
      if (!alreadySaved) {
        await pricingRuleService.createRoom({
          pricingRuleId: rule.id,
          roomId,
          pricingMethod: "FIXED",
          pricingValue,
          isEnabled: true,
        });
      }
    }
  }

  async function hydrateTier(row) {
    photos.clearPhotos();
    const match = properties.find(
      (property) => property.uuid === row?.propertyUuid || property.name === row?.propertyName
    );
    if (match?.uuid) setForm((current) => ({ ...current, propertyUuid: match.uuid }));
    const key = roomKey(row);
    setTierKey(key);
    if (!key) {
      setTier(emptyTier());
    } else {
      seasonService
        .load()
        .then(({ tiers }) => setTier(normalizeTier(tiers?.[key], regularRatesFor(row))))
        .catch(() => setTier(emptyTier()));
    }
    if (row?.id) {
      try {
        const detail = await roomService.get(row.id);
        photos.setPhotosFromRow(photoStateForRow(detail));
        setForm((current) => ({ ...current, ...detail, roomName: current.roomName, propertyUuid: current.propertyUuid }));
      } catch (err) {
        notify(err?.message || "Could not load photos for this room.");
      }
    }
  }

  function resetTier() {
    setTier(emptyTier());
    setTierKey("");
    photos.clearPhotos();
  }

  async function remove(id) {
    if (!window.confirm("Delete this room?")) return;
    try {
      await roomService.remove(id);
      notify("Room deleted.");
    } catch (err) {
      console.error("Room delete error:", err);
      notify(err?.message || "Could not delete room.");
    }
    load();
  }

  async function restore(uuid) {
    try {
      await roomService.restore(uuid);
      notify("Room restored.");
    } catch (err) {
      console.error("Room restore error:", err);
      notify(err?.message || "Could not restore room.");
    }
    load();
  }

  const regular = regularRatesFor(form);

  return (
    <CrudPanel
      title="Room"
      sectionLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><BedDouble size={18} />Room Master</span>}
      header="Room Inventory"
      rows={rows}
      loading={loading}
      saving={saving}
      form={form}
      setForm={setForm}
      emptyForm={emptyRoom}
      save={save}
      remove={remove}
      fields={formFields}
      firstRowFields={["propertyUuid", "roomName", "roomNumber"]}
        hideFormFields={["propertyName", "basePrice", "b2bPrice", "sellingPrice", "enableForAgents", "isDeleted"]}
        requiredFields={["propertyUuid", "roomName", "roomNumber"]}
        numberFields={["maxOccupancy", "basePrice", "b2bPrice", "sellingPrice"]}
        collapsible={true}
        showForm={showForm}
        setShowForm={setShowForm}
        selectFields={{
          propertyUuid: propertyOptions,
          roomType: defaultRoomTypes,
          bedType: defaultBedTypes,
          enableForAgents: [{ value: true, label: "Enabled" }, { value: false, label: "Disabled" }],
        }}
        fieldLabels={{ propertyUuid: "Property" }}
        buttonLabel="Room"
      currencyFields={["basePrice", "b2bPrice", "sellingPrice"]}
      colorFields={PRICE_COLORS}
        hideTableFields={["propertyUuid"]}
        columnLabels={{ enableForAgents: "Enable for Agent" }}
        onEdit={hydrateTier}
        onClear={resetTier}
        onRestore={restore}
        onFieldChange={(key, value) => {
          if (key === "propertyUuid" || key === "roomName") {
            hydrateTier({ ...form, [key]: value });
          }
        }}
        extraContent={
          <>
          <div className="seasonal-block">
            <p className="section-label">Regular Pricing</p>
            <div className="field-grid cols-2">
              {PRICE_FIELDS.map(({ key, label, formKey }) => (
                <label key={key}>
                  <span style={{ color: PRICE_COLORS[key] }}>{label}</span>
                  <input
                    type="number"
                    min="0"
                    placeholder={label}
                    value={form[formKey] || ""}
                    onChange={(e) => setForm({ ...form, [formKey]: e.target.value })}
                  />
                </label>
              ))}
            </div>
            <p className="image-hint">
              Weekend, holiday and peak season rates are calculated from the selling price above.
            </p>
          </div>
          <div className="image-upload-area seasonal-block">
            <p className="section-label">Seasonal Pricing</p>
            {!tierKey ? (
              <p className="image-hint">
                Enter a property and room name above to set weekend, holiday and peak season rates.
              </p>
            ) : (
              <>
                <p className="image-hint">
                  Leave a price at 0 to use the regular price above. Peak season overrides
                  holiday, which overrides weekend.
                </p>
                <div className="tier-table">
                  <div className="tier-table-head">
                    <span>Season</span>
                    {PRICE_FIELDS.map(({ key, label }) => (
                      <span key={key}>{label}</span>
                    ))}
                  </div>
                  {TIER_FIELDS.map((season) => (
                    <div className="tier-table-row" key={season} data-season={season}>
                      <span className="tier-cell-label">{TIER_LABELS[season]}</span>
                      {PRICE_FIELDS.map(({ key, label }) => {
                        const raw = Number(tier[season]?.[key] || 0);
                        const fallback = regular[key];
                        return (
                          <div className="tier-table-cell" key={key}>
                            <input
                              type="number"
                              min="0"
                              value={tier[season]?.[key] || ""}
                              placeholder={String(fallback || 0)}
                              onChange={(e) => setTier({
                                ...tier,
                                [season]: { ...tier[season], [key]: e.target.value },
                              })}
                            />
                            <span className="tier-cell-result">
                              {raw === 0
                                ? `uses ${money(fallback)}`
                                : `${money(raw)} per night`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="seasonal-block">
            <label className="special-day-row">
              <input
                type="checkbox"
                checked={form.enableForAgents === true}
                onChange={(e) => setForm({ ...form, enableForAgents: e.target.checked })}
              />
              <span>Enable for Agents</span>
            </label>
            <p className="image-hint">
              When enabled, agents can see and book this room and the B2B price applies to their
              bookings.
            </p>
          </div>
          <PhotoUploader label="Room Photos" className="image-upload-area seasonal-block" {...photos} />
          </>
        }
    />
  );
}
